import { useState, useEffect } from "react";

export default <T,>(
  key: string,
  defaultValue: T,
): [T, (value: T | ((t: T) => T)) => void] => {
  // Deserialize the JSON stored in sessionStorage to retrieve initial state
  const readValueFromSessionStorage = (): T => {
    if (typeof window === "undefined") {
      return defaultValue;
    }

    try {
      const item = window.sessionStorage.getItem(key);
      return item ? JSON.parse(item) : defaultValue;
    } catch (error) {
      console.warn(`Error reading sessionStorage key “${key}”:`, error);
      return defaultValue;
    }
  };

  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState<T>(
    readValueFromSessionStorage,
  );

  // Update session storage when state changes
  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    try {
      const serializedState = JSON.stringify(storedValue);
      window.sessionStorage.setItem(key, serializedState);
    } catch (error) {
      console.warn(`Error setting sessionStorage key “${key}”:`, error);
    }
  }, [key, storedValue]);

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to sessionStorage.
  const setValue = (value: T | ((t: T) => T)) => {
    if (typeof window === "undefined") {
      console.warn(
        `Tried setting sessionStorage key “${key}” even though environment does not support sessionStorage.`,
      );
      return;
    }

    try {
      // Allow value to be a function so we have the same API as useState
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      // Save state
      setStoredValue(valueToStore);
      // Save to session storage
      window.sessionStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.warn(`Error setting sessionStorage key “${key}”:`, error);
    }
  };

  return [storedValue, setValue];
};
